/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Notify from "@eisinc/App/Components/Notify";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";


const enum fields {
    videoSrc = "videoSrc",
    videoW = "videoW",
    videoH = "videoH",
    description = "description",
    uploadDate = "uploadDate",
    duration  = "duration"
}

interface CloudinaryVideoProps extends WidgetProps {
    fields: {
        [fields.videoSrc]: string;
        [fields.videoW]: string;
        [fields.videoH]: string;
        [fields.description]: string;
        [fields.uploadDate]: string;
        [fields.duration]: string;
    };
}

type Props = CloudinaryVideoProps;

export interface CloudinaryVideoStyles {
    videoWrapper?: InjectableCss;
}

export const cloudinaryVideoStyles: CloudinaryVideoStyles = {
    videoWrapper: {
        css: css`
            width: 100%;
            max-width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;

            video {
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
};

const styles = cloudinaryVideoStyles;

const cloudinaryVideoWidget: React.FC<Props> = ({ fields }) => {
    const { videoSrc, videoW, videoH } = fields;

    let isModifiedUrl = false;
    let isValidUrl = false;

    
    let videoSrcWithFinalTransformations;
    let videoName = "";

    if(videoSrc && videoSrc.toLowerCase().indexOf("/upload/") > -1) {
        isValidUrl = true;
        const srcSplittedArray = videoSrc.toLowerCase().split("/upload/");
        const cloudinaryUrlPart = srcSplittedArray[0];
        const videoFormatPartSpit = srcSplittedArray[1].split(".");
        const videoTransformationsAndIdPart = videoFormatPartSpit[0];
        const videoFormatPart = videoFormatPartSpit[1];
        const videoTransformations = `q_auto,f_${videoFormatPart}${videoW ? `,w_${videoW}`: ""}${videoH ? `,h_${videoH}`: ""}`;
        const splittedVideoTransformationsAndIdPart = videoTransformationsAndIdPart.split("/");

        splittedVideoTransformationsAndIdPart.forEach((value, i) => {
            if(value.indexOf("f_") > -1 || value.indexOf("w_") > -1 || value.indexOf("q_") > -1) {
                isModifiedUrl = true;   
                splittedVideoTransformationsAndIdPart[i] = videoTransformations;
            }
            if(i === splittedVideoTransformationsAndIdPart.length-1){
                videoName = splittedVideoTransformationsAndIdPart[i];
            }
        });

        if(!isModifiedUrl) {
            splittedVideoTransformationsAndIdPart.unshift(`${videoTransformations}`);
        }

        const finalJoindVideoTransformationsAndIdPart = splittedVideoTransformationsAndIdPart.join("/");
        videoSrcWithFinalTransformations = `${cloudinaryUrlPart}/upload/${finalJoindVideoTransformationsAndIdPart}`;
    } else {
        isValidUrl = false;
    }

    return (
        <>
            {!isValidUrl && (
                <Notify
                    messageType="danger"
                    body={siteMessage("Custom_NotValidPath")}
                />
            )}   
            <StyledWrapper {...styles.videoWrapper} itemScope itemType="https://schema.org/VideoObject">
                <video width={videoW || "100%"} height={videoH || "100%"} autoPlay muted controls poster="false">
                    <source src={videoSrcWithFinalTransformations} />
                    <meta itemProp="name" content={videoName} />
                    <meta itemProp="caption" content={fields.description} />
                    <meta itemProp="uploadDate" content={fields.uploadDate} />
                    <meta itemProp="duration" content={fields.duration} />
                </video>
            </StyledWrapper>
        </>
    );
};

export const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    fieldDefinitions: [
        {
            name: fields.videoSrc,
            displayName: "Video Src",
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.videoW,
            displayName: "Video Width",
            editorTemplate: "IntegerField",
            defaultValue: 600,
            fieldType: "General",
            placeholder: "ُEnter video width in px",
            min: 1,
        },
        {
            name: fields.videoH,
            displayName: "Video Height",
            editorTemplate: "IntegerField",
            defaultValue: 400,
            fieldType: "General",
            placeholder: "ُEnter video height in px",
            min: 1,
        },
        {
            name: fields.description,
            displayName: "Description",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: fields.uploadDate,
            displayName: "Upload Date",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: fields.duration,
            displayName: "Duration",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
        }
    ],
};

const widgetModule: WidgetModule = {
    component: cloudinaryVideoWidget,
    definition,
};

export default widgetModule;
